import mqtt from 'mqtt';

import { MQTT_HOSTNAME, MQTT_PATH, MQTT_PORT, MQTT_PROTOCOL } from '@/configs/environment';
import { parseMessage } from '@/features/console/types';
import { useAuthStore } from '@/store/authStore';
import { useMqttStore } from '@/store/mqttStore';

class MqttService {
  private client: ReturnType<typeof mqtt.connect> | null = null;
  private messageCounter = 0;

  connect(): void {
    if (this.client) return; // client exists (possibly reconnecting) — mqtt.js handles reconnect automatically

    const { user } = useAuthStore.getState();
    if (!user) return;

    const wsProtocol = MQTT_PROTOCOL === 'mqtts' ? 'wss' : 'ws';
    const brokerUrl = `${wsProtocol}://${MQTT_HOSTNAME}:${MQTT_PORT}${MQTT_PATH}`;

    this.client = mqtt.connect(brokerUrl, {
      username: user.username,
      password: `token:[${user.token}]`,
      clientId: `${user.username}::${Math.random().toString(36).slice(2, 11)}`,
      keepalive: 60,
      clean: true,
      reconnectPeriod: 1000,
    });

    this.client.on('connect', () => {
      useMqttStore.getState().setConnected(true);
    });

    this.client.on('error', () => {
      useMqttStore.getState().setConnected(false);
    });

    this.client.on('offline', () => {
      useMqttStore.getState().setConnected(false);
    });

    this.client.on('message', (topic: string, raw: Buffer) => {
      this.messageCounter += 1;
      const msg = parseMessage(topic, raw.toString(), this.messageCounter);
      useMqttStore.getState().addMessage(msg);
    });
  }

  disconnect(): void {
    if (this.client) {
      this.client.end();
      this.client = null;
    }
    useMqttStore.getState().setConnected(false);
  }

  addTopics(topics: string[]): void {
    if (!this.client) return;
    topics.forEach((t) => this.client!.subscribe(t));
  }

  removeTopics(topics: string[]): void {
    if (!this.client) return;
    topics.forEach((t) => this.client!.unsubscribe(t));
  }
}

export const mqttService = new MqttService();
